/**
 * MedicalInfoStep - Step 3: Medical and emergency contact information (supports multiple siblings)
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Heart, ArrowRight, ArrowLeft, Users, User } from 'lucide-react';

export const MedicalInfoStep = ({ 
  medicalForm, 
  setMedicalForm, 
  medicalForms = [],
  setMedicalForms,
  allStudents = [],
  hasSiblings = false,
  onNext, 
  onBack 
}) => {
  // Update a specific medical form field
  const updateMedicalForm = (index, field, value) => {
    if (setMedicalForms) {
      const updated = [...medicalForms];
      updated[index] = { ...updated[index], [field]: value };
      setMedicalForms(updated);
    }
    // Also update legacy single form if it's the first student
    if (index === 0 && setMedicalForm) {
      setMedicalForm({ ...medicalForm, [field]: value });
    }
  };

  const handleNext = () => {
    // Validate emergency contact (shared for all students)
    if (!medicalForm.emergency_contact || !medicalForm.emergency_phone) {
      toast.error('Please provide emergency contact details');
      return;
    }
    onNext();
  };

  // Multi-student UI
  if (hasSiblings && medicalForms.length > 1) {
    return (
      <div className="space-y-6">
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-purple-400 font-semibold">
                  Medical Information for {medicalForms.length} Students
                </p>
                <p className="text-purple-400/70 text-sm">
                  Please provide medical details for each student.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {medicalForms.map((form, index) => (
          <Card key={form.id || index} className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF007F]" />
                {form.name || `Student ${index + 1}`}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white/70">Medical Conditions</Label>
                <Textarea
                  value={form.conditions}
                  onChange={(e) => updateMedicalForm(index, 'conditions', e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="List any medical conditions, disabilities, or special needs..."
                  rows={2}
                  data-testid={`medical-conditions-${index}`}
                />
              </div>
              
              <div>
                <Label className="text-white/70">Allergies</Label>
                <Textarea
                  value={form.allergies}
                  onChange={(e) => updateMedicalForm(index, 'allergies', e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="List any allergies..."
                  rows={2}
                  data-testid={`medical-allergies-${index}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Shared Emergency Contact */}
        <Card className="bg-red-500/10 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-400" />
              Emergency Contact (for all students) *
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white/70">Contact Name *</Label>
              <Input
                value={medicalForm.emergency_contact}
                onChange={(e) => setMedicalForm({ ...medicalForm, emergency_contact: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Emergency contact name"
                data-testid="emergency-contact"
              />
            </div>
            <div>
              <Label className="text-white/70">Contact Phone *</Label>
              <Input
                type="tel"
                value={medicalForm.emergency_phone}
                onChange={(e) => setMedicalForm({ ...medicalForm, emergency_phone: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Emergency contact phone"
                data-testid="emergency-phone"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleNext}
            className="flex-1 bg-[#FF007F] hover:bg-[#D6006A] text-white"
            data-testid="step3-continue"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Single student UI (original)
  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#FF007F]" />
          Medical & Emergency Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-white/70">Medical Conditions</Label>
          <Textarea
            value={medicalForm.conditions}
            onChange={(e) => setMedicalForm({ ...medicalForm, conditions: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
            placeholder="List any medical conditions, disabilities, or special needs..."
            rows={3}
            data-testid="medical-conditions"
          />
        </div>
        
        <div>
          <Label className="text-white/70">Allergies</Label>
          <Textarea
            value={medicalForm.allergies}
            onChange={(e) => setMedicalForm({ ...medicalForm, allergies: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
            placeholder="List any allergies..."
            rows={2}
            data-testid="medical-allergies"
          />
        </div>

        <div className="pt-4 border-t border-white/10">
          <p className="text-sm font-medium text-white/70 mb-3">Emergency Contact *</p>
          <div>
            <Label className="text-white/70">Contact Name *</Label>
            <Input
              value={medicalForm.emergency_contact}
              onChange={(e) => setMedicalForm({ ...medicalForm, emergency_contact: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Emergency contact name"
              data-testid="emergency-contact"
            />
          </div>
          <div className="mt-2">
            <Label className="text-white/70">Contact Phone *</Label>
            <Input
              type="tel"
              value={medicalForm.emergency_phone}
              onChange={(e) => setMedicalForm({ ...medicalForm, emergency_phone: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Emergency contact phone"
              data-testid="emergency-phone"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleNext}
            className="flex-1 bg-[#FF007F] hover:bg-[#D6006A] text-white"
            data-testid="step3-continue"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicalInfoStep;
